/** Object specific version of `map` */
export const mapObj = (f, obj) =>
    Object.fromEntries(
        Object.entries(obj ?? {}).map(([key, val], i) => [key, f(key, val, i, obj)])
    );

/**
 * Transforms object with mapper that may return promise
 * @param {(key: string, val: any, i: number, obj: Object) => any} asyncMapper - mapper that transforms each value (returns promise or just simple value)
 * @param {Object} obj
 * @returns {Promise.<Object>} promise containing the transformed object
 */
export const mapObjPromised = (asyncMapper, obj) =>
    Promise.all(
        Object.entries(obj).map(([key, val], i) =>
            Promise.resolve(asyncMapper(key, val, i, obj)).then(res => [key, res])
        )
    ).then(Object.fromEntries);

/** Object specific version of `filter` */
export const filterObj = (f, obj) =>
    Object.fromEntries(
        Object.entries(obj ?? {})
            .filter(([key, val], i) => f(key, val, i, obj))
            .map(([key, val]) => [key, val])
    );

/**
 * Rewrites options that only exist in the first obj with the values of the second obj
 * @param {Object} a - object with default values
 * @param {Object} b - object with values to rewrite
 * @returns {Object} new object
 */
export const mergeWithDefault = (a = {}, b = {}) => {
    if (!a) return b;

    if (!b) return a;

    const keys = Object.keys(a);
    return Object.fromEntries(
        keys.map(key => {
            const val = key in b ? b[key] : a[key];
            return [key, val && typeof val === 'object' ? mergeWithDefault(a[key], b[key]) : val];
        })
    );
};

export const isBrowser = () =>
    Boolean(typeof globalThis !== 'undefined' && globalThis.self && globalThis.navigator);

export const isIE = () => typeof window === 'object' && 'ActiveXObject' in window;

// IE doesn't support es6 Proxy which is used in idb package. Also, there is Google
// Proxy polyfill, but it has limited functionality, which is not enough for idb.
export const isIdbAvailable = () => isBrowser() && typeof Proxy !== 'undefined';

export const downloadFile = (url, name) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    if (name) {
        anchor.download = name;
    }

    document.body.appendChild(anchor);

    if (typeof MouseEvent === 'function') {
        anchor.dispatchEvent(
            new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
            })
        );
    } else {
        const event = document.createEvent('Event');
        event.initEvent('click', true, true);
        anchor.dispatchEvent(event);
    }

    document.body.removeChild(anchor);
};

/** Returns random element from the list */
export const randomFrom = xs => xs[Math.floor(Math.random() * xs.length)];

export const preventUnhandledRejection = promise => promise.catch(() => {});

const memoizeLast = f => {
    let lastArg = null;
    let lastResult;
    return arg => {
        if (arg === lastArg) return lastResult;

        lastArg = arg;
        lastResult = f(arg);
        return lastResult;
    };
};

/**
 * Returns the same serialized array if the given message parts array is the same.
 * This function is useful when serializing the same message in different transports.
 */
export const serializeMsgPartsMemoized = memoizeLast(messageParts =>
    messageParts.map(part => {
        if (typeof part !== 'object' || !part) return part;

        if (part instanceof Error) {
            // Error
            return {
                name: part.name,
                message: part.message,
                constructorName: part.constructor?.name || '',
                stack: part.stack,
            };
        }

        if (part.window === part) {
            // Window
            return {
                name: part.name,
                href: part.location?.href,
                top: part === part.top,
                closed: part.closed,
            };
        }

        if (part.ownerDocument) {
            // HTMLElement
            return {
                nodeName: part.nodeName,
                docUri: part.ownerDocument?.documentURI,
                docTitle: part.ownerDocument?.title,
                docVisibilityState: part.ownerDocument?.visibilityState,
            };
        }

        if (Symbol.iterator in Object(part) && (part instanceof Map || part instanceof Set)) {
            return Array.from(part);
        }

        if (
            (typeof WeakSet !== 'undefined' && part instanceof WeakSet) ||
            (typeof WeakMap !== 'undefined' && part instanceof WeakMap)
        ) {
            return '[WeakCollection]';
        }

        if (part.toString) {
            // etc
            return part;
        }

        return '[non-serializable value]';
    })
);

/**
 * Helps to sort logs in the order of their logging.
 * @param {LoggerFormattedMessage} a - first log
 * @param {LoggerFormattedMessage} b - second log
 * @returns {number} - negative if a is logged before b, positive if a is logged after b, 0 if they are simultaneous.
 */
export const logsComparator = (a, b) => {
    const aTime = a.tsz ? Date.parse(a.tsz) : -Infinity;
    const bTime = b.tsz ? Date.parse(b.tsz) : -Infinity;

    return aTime - bTime || (a.seq ?? -1) - (b.seq ?? -1);
};
